import { useQuery, useQueryClient } from '@tanstack/react-query'
import { groups } from '../lib/api'

/** Query keys for the groups domain (shared so mutations can invalidate consistently). */
export const groupKeys = {
  mine: ['community', 'groups'] as const,
  detail: (id: string | undefined) => ['community', 'group', id] as const,
  composableCircles: ['community', 'group-composable-circles'] as const,
  joinRequests: (id: string | undefined) => ['community', 'group-join-requests', id] as const,
}

/** GET /groups/{id} — full group detail. Disabled until an id is present. */
export function useGroupDetail(id: string | undefined) {
  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: () => groups.getGroup(id as string),
    enabled: !!id,
    retry: false,
  })
}

/** GET /groups/my-circles — the caller's own circles for the create/join multi-select. */
export function useMyComposableCircles(enabled = true) {
  return useQuery({
    queryKey: groupKeys.composableCircles,
    queryFn: groups.getMyComposableCircles,
    enabled,
    retry: false,
  })
}

/** GET /groups/{id}/join-requests — pending host-facing vouch requests. */
export function useGroupJoinRequests(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: groupKeys.joinRequests(id),
    queryFn: () => groups.listJoinRequests(id as string),
    enabled: !!id && enabled,
    retry: false,
  })
}

/** Invalidate every groups query after a mutation. */
export function useInvalidateGroups() {
  const qc = useQueryClient()
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['community', 'me'] })
    void qc.invalidateQueries({ queryKey: ['community', 'approvals'] })
    void qc.invalidateQueries({ queryKey: groupKeys.mine })
    void qc.invalidateQueries({ queryKey: groupKeys.composableCircles })
    void qc.invalidateQueries({ queryKey: ['community', 'group-invitations'] })
    void qc.invalidateQueries({ queryKey: ['community', 'browse'] })
    if (id) {
      void qc.invalidateQueries({ queryKey: groupKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: groupKeys.joinRequests(id) })
    }
  }
}
